import { useTranslation } from '@cobliteam/i18n';
import Divider from '@cobliteam/ui/components/Divider';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { formatPhoneNumber } from '@cobliteam/ui-business/utils/phoneNumber';
import { DEFAULT_LOCALE, formatDistanceToNow, parseISO } from '@cobliteam/utils/date';
import type { DriverDetailsData } from './mockInvestigation';

const formatLocationUpdated = (rawTimestamp: string): string => {
  if (!rawTimestamp) return '';
  const updatedAt = parseISO(rawTimestamp);
  if (Number.isNaN(updatedAt.getTime())) return '';
  return formatDistanceToNow(updatedAt, { locale: DEFAULT_LOCALE, addSuffix: true });
};

interface RiskRecognitionDriverDetailsProps {
  data: DriverDetailsData;
  onViewRoute: () => void;
  /** Analytics only — the anchor handles the navigation. */
  onTalkToDriver: () => void;
}

const RiskRecognitionDriverDetails = ({ data, onViewRoute, onTalkToDriver }: RiskRecognitionDriverDetailsProps) => {
  const { t } = useTranslation('ai');
  const whatsAppUrl = data.whatsAppUrl;
  const locationUpdatedLabel = formatLocationUpdated(data.locationUpdatedAt);

  return (
    <section className='flex shrink-0 flex-col gap-5 rounded-[18px] border border-[#e4e4e7] border-solid bg-white py-6 shadow-[0px_1px_3px_rgba(0,27,184,0.05),0px_1px_2px_-1px_rgba(0,27,184,0.1)]'>
      <header className='px-6'>
        <Typography variant='text-lg-semibold' className='text-[#09090b]'>
          {t('risk_recognition.drawer.driver_details_title')}
        </Typography>
      </header>

      <div className='flex flex-col px-6'>
        <div className='flex flex-col gap-2'>
          <span className='inline-flex flex-wrap items-center gap-2'>
            <Icon name='square-user' size='sm' className='shrink-0 text-[#71717a]' aria-hidden />
            <Typography as='span' variant='text-md' className='text-[#71717a]'>
              {data.driverName}
            </Typography>
            {whatsAppUrl && (
              <>
                <span aria-hidden className='text-[#a1a1aa]'>
                  •
                </span>
                <a
                  href={whatsAppUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={onTalkToDriver}
                  aria-label={t('risk_recognition.drawer.whatsapp_aria')}
                  className='inline-flex items-center gap-1.5 rounded-sm text-[#1c53e6] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
                >
                  <Icon name='cb-whatsapp' size='sm' className='shrink-0' aria-hidden />
                  <Typography as='span' variant='text-md'>
                    {formatPhoneNumber(data.driverPhone)}
                  </Typography>
                </a>
              </>
            )}
          </span>
          <span className='inline-flex items-center gap-2'>
            <Icon name='truck' size='sm' className='shrink-0 text-[#71717a]' aria-hidden />
            <Typography as='span' variant='text-md' className='text-[#71717a]'>
              {data.vehicleLabel}
            </Typography>
          </span>
        </div>

        <Divider className='mt-[18px] mb-[10px]' />

        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-start gap-2.5'>
            <Icon name='map-pin' size='sm' className='mt-0.5 shrink-0 text-[#71717a]' aria-hidden />
            <div className='flex flex-col gap-1'>
              <Typography variant='text-sm' className='text-[#71717a]'>
                {locationUpdatedLabel
                  ? t('risk_recognition.drawer.location_caption', { relativeTime: locationUpdatedLabel })
                  : t('risk_recognition.drawer.location_caption_no_time')}
              </Typography>
              <Typography variant='text-md' className='text-[#1c2024]'>
                {data.locationAddress}
              </Typography>
            </div>
          </div>

          <button
            type='button'
            onClick={onViewRoute}
            className='inline-flex h-9 shrink-0 items-center rounded-lg bg-[#dcebfe] px-4 transition-colors hover:bg-[#cfe2fb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
          >
            <Typography as='span' variant='text-md-medium' className='whitespace-nowrap text-[#07074f]'>
              {t('risk_recognition.drawer.view_route')}
            </Typography>
          </button>
        </div>
      </div>
    </section>
  );
};

export default RiskRecognitionDriverDetails;
